import loadable from "@loadable/component"
import { Navigate, Route } from "react-router-dom"

import MerchantLoading from "components/merchant-loading"
import { jewlr_t, retry, IS_GE_CHECKOUT, IS_NA } from "helpers/application"
import { enableKlarnaCheckout } from "helpers/checkout/checkout_options"
import { Routes } from "helpers/sentry"

import InfoRouteWrapper from "./route/info-route-wrapper"

const loadableDefaultOptions = {
  fallback: <MerchantLoading />,
}

const GenericNotFound = loadable(
  () => retry(() => import("app/generic-not-found")),
  loadableDefaultOptions
)
const GiftCard = loadable(
  () => retry(() => import("areas/info/gift-card")),
  loadableDefaultOptions
)
const Hallmark = loadable(
  () => retry(() => import("areas/info/hallmark")),
  loadableDefaultOptions
)
const Klarna = loadable(
  () => retry(() => import("areas/info/klarna")),
  loadableDefaultOptions
)
const KlarnaAffirm = loadable(
  () => retry(() => import("areas/info/klarna_affirm")),
  loadableDefaultOptions
)
const ReturnPolicy = loadable(
  () => retry(() => import("areas/info/return-policy")),
  loadableDefaultOptions
)
const AccessibilityPlan = loadable(
  () => retry(() => import("../accessibility-plan")),
  loadableDefaultOptions
)
const AccessibilityStatement = loadable(
  () => retry(() => import("../accessibility-statement")),
  loadableDefaultOptions
)
const Affirm = loadable(
  () => retry(() => import("../affirm")),
  loadableDefaultOptions
)
const WarrantyInformation = loadable(
  () => retry(() => import("../warranty/index")),
  loadableDefaultOptions
)
const ChainsInformation = loadable(
  () => retry(() => import("./chains-information")),
  loadableDefaultOptions
)
const CorporateSales = loadable(
  () => retry(() => import("./corporate-sales")),
  loadableDefaultOptions
)
const Diamond = loadable(
  () => retry(() => import("./diamond")),
  loadableDefaultOptions
)
const DiamondCuration = loadable(
  () => retry(() => import("./diamond-curation")),
  loadableDefaultOptions
)
const Gemstones = loadable(
  () => retry(() => import("./gemstones")),
  loadableDefaultOptions
)
const HowItsMade = loadable(
  () => retry(() => import("./how-its-made")),
  loadableDefaultOptions
)
const InstantGiftExplained = loadable(
  () => retry(() => import("areas/instant-gift/explained")),
  loadableDefaultOptions
)
const Packaging = loadable(
  () => retry(() => import("./packaging")),
  loadableDefaultOptions
)
const Partnerships = loadable(
  () => retry(() => import("./partnerships")),
  loadableDefaultOptions
)
const Press = loadable(
  () => retry(() => import("./press")),
  loadableDefaultOptions
)
const DisruptiveRing = loadable(
  () => retry(() => import("./press/disruptive-ring")),
  loadableDefaultOptions
)
const MostInnovative = loadable(
  () => retry(() => import("./press/most-innovative")),
  loadableDefaultOptions
)
const PersonalTouch = loadable(
  () => retry(() => import("./press/personal-touch")),
  loadableDefaultOptions
)
const PurePlay = loadable(
  () => retry(() => import("./press/pureplay")),
  loadableDefaultOptions
)
const SketchFab = loadable(
  () => retry(() => import("./press/sketchfab")),
  loadableDefaultOptions
)
const PrivacyNotice = loadable(
  () => retry(() => import("./privacy-notice")),
  loadableDefaultOptions
)
const PrivacyPolicy = loadable(
  () => retry(() => import("./privacy-policy")),
  loadableDefaultOptions
)
const RingSizing = loadable(
  () => retry(() => import("./ring-sizing")),
  loadableDefaultOptions
)
const ShippingInformation = loadable(
  () => retry(() => import("./shipping-information")),
  loadableDefaultOptions
)
const SMSPrivacyPolicy = loadable(
  () => retry(() => import("./sms-privacy-policy")),
  loadableDefaultOptions
)
const SMSTermsConditions = loadable(
  () => retry(() => import("./sms-terms-conditions")),
  loadableDefaultOptions
)
const TermsConditions = loadable(
  () => retry(() => import("./terms-conditions")),
  loadableDefaultOptions
)
const VipTermsConditions = loadable(
  () => retry(() => import("./vip-terms-conditions")),
  loadableDefaultOptions
)
const WatchCare = loadable(
  () => retry(() => import("./watches/care")),
  loadableDefaultOptions
)
const WatchSizing = loadable(
  () => retry(() => import("./watches/sizing")),
  loadableDefaultOptions
)
const WhyChooseJewlr = loadable(
  () => retry(() => import("./why-choose-jewlr")),
  loadableDefaultOptions
)

const Info = () => {
  const is_ge_checkout = IS_GE_CHECKOUT()

  return (
    <Routes>
      <Route
        element={
          <Navigate
            replace
            to={
              sl.config.enable_gift_preview_video ? "/gift-preview-video" : "/"
            }
          />
        }
        path="/instant_gift"
      />
      <Route
        element={
          <InfoRouteWrapper layout="company" showCta title="Why Choose Jewlr">
            <WhyChooseJewlr />
          </InfoRouteWrapper>
        }
        path="/why_choose_jewlr"
      />
      {sl.config.enable_gift_preview_video ? (
        <Route
          element={
            <InfoRouteWrapper showCta title="Gift Preview Video">
              <InstantGiftExplained />
            </InfoRouteWrapper>
          }
          path="/gift-preview-video"
        />
      ) : (
        <Route
          element={<Navigate replace to="/" />}
          path="/gift-preview-video"
        />
      )}
      <Route
        element={
          <InfoRouteWrapper
            layout="press"
            newLayout
            title="Jewlr Wins 2018 Pure Play of the Year Award"
          >
            <PurePlay />
          </InfoRouteWrapper>
        }
        path="/press/jewlr_wins_2018_pure_play_awards"
      />
      <Route
        element={
          <InfoRouteWrapper
            layout="press"
            newLayout
            title="Sketchfab Disrupts Traditional Banner Advertising"
          >
            <SketchFab />
          </InfoRouteWrapper>
        }
        path="/press/sketchfab_disrupts_tranditional_adv"
      />
      <Route
        element={
          <InfoRouteWrapper
            layout="press"
            newLayout
            title="Tony Davis Praises Jewlr.com's Personal Touch"
          >
            <PersonalTouch />
          </InfoRouteWrapper>
        }
        path="/press/tony_davis_praises_jewlr_personal_touch"
      />
      <Route
        element={
          <InfoRouteWrapper
            layout="press"
            newLayout
            title="Online Jeweller Business Has A Disruptive Ring"
          >
            <DisruptiveRing />
          </InfoRouteWrapper>
        }
        path="/press/online_jeweller_has_a_disruptive_ring"
      />
      <Route
        element={
          <InfoRouteWrapper
            layout="press"
            newLayout
            title="Jewlr Wins 2013 Most Innovative Retailer Award"
          >
            <MostInnovative />
          </InfoRouteWrapper>
        }
        path="/press/jewlr_named_innovative_retailer_in_2013"
      />
      <Route
        element={
          <InfoRouteWrapper layout="company" title="Press">
            <Press />
          </InfoRouteWrapper>
        }
        path="/press"
      />
      <Route
        element={
          <InfoRouteWrapper removeLayout title="Partnerships">
            <Partnerships />
          </InfoRouteWrapper>
        }
        path="/partnerships"
      />
      <Route
        element={
          <InfoRouteWrapper newLayout title="Terms and Conditions">
            <TermsConditions />
          </InfoRouteWrapper>
        }
        path="/terms-conditions"
      />
      {IS_NA() && (
        <Route
          element={
            <InfoRouteWrapper newLayout title="Messaging Privacy Policy">
              <SMSPrivacyPolicy />
            </InfoRouteWrapper>
          }
          path="/sms-privacy-policy"
        />
      )}
      {IS_NA() && (
        <Route
          element={
            <InfoRouteWrapper newLayout title="Messaging Terms and Conditions">
              <SMSTermsConditions />
            </InfoRouteWrapper>
          }
          path="/sms-terms-conditions"
        />
      )}
      <Route
        element={
          <InfoRouteWrapper newLayout title="Privacy Policy">
            <PrivacyPolicy />
          </InfoRouteWrapper>
        }
        path="/privacy-policy"
      />
      <Route
        element={
          <InfoRouteWrapper
            hidePageTitle
            newLayout
            title="Web Accessibility Statement"
          >
            <AccessibilityStatement />
          </InfoRouteWrapper>
        }
        path="/accessibility-statement"
      />
      <Route
        element={
          <InfoRouteWrapper
            hidePageTitle
            newLayout
            title="Web Accessibility Plan"
          >
            <AccessibilityPlan />
          </InfoRouteWrapper>
        }
        path="/accessibility-plan"
      />
      <Route
        element={
          <InfoRouteWrapper newLayout title="Privacy Notice">
            <PrivacyNotice />
          </InfoRouteWrapper>
        }
        path="/privacy_notice"
      />
      <Route
        element={
          <InfoRouteWrapper newLayout showCta title="Corporate Sales">
            <CorporateSales />
          </InfoRouteWrapper>
        }
        path="/corporate_sales"
      />
      <Route
        element={
          <InfoRouteWrapper
            description={`Learn about the quick and easy process of creating custom, high-quality ${jewlr_t("terms.personalized")} ${jewlr_t("terms.jewelry")} in rings, necklaces, earrings, and more from Jewlr.`}
            heading={`How Custom ${jewlr_t("terms.Jewelry")} Is Made`}
            layout="learn"
            showCta
            title={`How ${jewlr_t("terms.Personalized")} Custom ${jewlr_t("terms.Jewelry")} Is Made`}
          >
            <HowItsMade />
          </InfoRouteWrapper>
        }
        path="/how_its_made"
      />
      <Route
        element={
          <InfoRouteWrapper
            description="Learn about our line of custom genuine gemstones, birthstones, simulated gemstones, and Brilliance Gemstone Collection from Jewlr."
            showCta
            title="Custom Gemstones Information"
          >
            <Gemstones />
          </InfoRouteWrapper>
        }
        path="/gemstones"
      />
      <Route
        element={
          <InfoRouteWrapper
            description={`Surprise the special people in your life with ${jewlr_t("terms.personalized")} custom ${jewlr_t("terms.jewelry")} packaging and gift wrapping with greeting cards and photo boxes from Jewlr.`}
            rebrand
            showCta
            title={`Custom ${jewlr_t("terms.Jewelry")} Packaging & Gift Wrapping`}
          >
            <Packaging />
          </InfoRouteWrapper>
        }
        path="/packaging"
      />
      {!is_ge_checkout && (
        <Route
          element={
            <InfoRouteWrapper hidePageTitle newLayout title="Jewlr Gift Cards">
              <GiftCard />
            </InfoRouteWrapper>
          }
          path="/gift_cards"
        />
      )}
      <Route
        element={
          <InfoRouteWrapper
            description={`Be sure you have the right size ring with our handy chart before you purchase your ${jewlr_t("terms.personalized")} custom-made ring from Jewlr.`}
            heading="Ring Sizing For Men & Women"
            newLayout
            showCta
            title="Ring Sizing Chart For Men & Women"
          >
            <RingSizing />
          </InfoRouteWrapper>
        }
        path="/ring_sizing"
      />
      <Route
        element={
          <InfoRouteWrapper
            rebrand
            showCta
            title={`${jewlr_t("terms.Shipping")} Information`}
          >
            <ShippingInformation />
          </InfoRouteWrapper>
        }
        path="/shipping_information"
      />
      <Route
        element={
          <InfoRouteWrapper
            description={`If, for any reason, you need to return or exchange your ${jewlr_t("terms.personalized")}, custom ${jewlr_t("jewelry")}, our return policy is available here for review from Jewlr.`}
            removeLayout
            title="Return Policy"
          >
            <ReturnPolicy />
          </InfoRouteWrapper>
        }
        path="/returns"
      />
      {sl.config.enable_rewards && (
        <Route
          element={
            <InfoRouteWrapper layout="company" title="VIP Terms and Conditions">
              <VipTermsConditions />
            </InfoRouteWrapper>
          }
          path="/vip-terms-conditions"
        />
      )}

      <Route
        element={
          <InfoRouteWrapper rebrand showCta title="Warranty Information">
            <WarrantyInformation />
          </InfoRouteWrapper>
        }
        path="/warranty-information"
      />

      {enableKlarnaCheckout() && !sl.config.enable_affirm && (
        <Route
          element={
            <InfoRouteWrapper title="Klarna">
              <Klarna />
            </InfoRouteWrapper>
          }
          path="/payment-plans"
        />
      )}
      {sl.config.enable_affirm && !enableKlarnaCheckout() && (
        <Route
          element={
            <InfoRouteWrapper showCta title="Affirm">
              <Affirm />
            </InfoRouteWrapper>
          }
          path="/payment-plans"
        />
      )}
      {sl.config.enable_affirm && enableKlarnaCheckout() && (
        <Route
          element={
            <InfoRouteWrapper title="Payment Plans">
              <KlarnaAffirm />
            </InfoRouteWrapper>
          }
          path="/payment-plans"
        />
      )}
      <Route
        element={
          <InfoRouteWrapper
            description="Find the perfect fit for your new custom necklace chain in length, color, karat, chain type, and thickness with this handy guide from Jewlr."
            newLayout
            showCta
            title="Necklace Chain Guide"
          >
            <ChainsInformation />
          </InfoRouteWrapper>
        }
        path="/chains_information"
      />
      <Route
        element={
          <InfoRouteWrapper
            description={`Learn about the "4 C's" of diamonds, cut, carat weight, color, and clarity, and what to look for in a custom, ${jewlr_t("terms.personalized")} piece of diamond ${jewlr_t("terms.jewelry")}, from Jewlr.`}
            title='What Are The "4 C&apos;s" Of Diamonds?'
          >
            <Diamond />
          </InfoRouteWrapper>
        }
        path="/diamond"
      />
      <Route
        element={
          <InfoRouteWrapper newLayout title="Diamond Curation">
            <DiamondCuration />
          </InfoRouteWrapper>
        }
        path="/diamond_curation"
      />
      <Route
        element={
          <InfoRouteWrapper title="Hallmark">
            <Hallmark />
          </InfoRouteWrapper>
        }
        path="/hallmark"
      />
      {sl.config.enable_watches && (
        <Route
          element={
            <InfoRouteWrapper title="Watch Resizing">
              <WatchSizing />
            </InfoRouteWrapper>
          }
          path="/watch-sizing"
        />
      )}
      {sl.config.enable_watches && (
        <Route
          element={
            <InfoRouteWrapper title="Watch Care">
              <WatchCare />
            </InfoRouteWrapper>
          }
          path="/watch-care"
        />
      )}
      <Route element={<GenericNotFound />} path="*" />
    </Routes>
  )
}

export default Info
